import { computed, ref, watch } from 'vue'

export type StepResolver = () => string | null

export interface WizardStepConfig {
    getNextStep?: StepResolver
}

export type WizardStepsConfig = Record<string, WizardStepConfig>

export const BASISROOSTER_SAVE_SCENARIO = {
    NEW_VERSION: 'new-version',
    EDIT_PLANNED: 'edit-planned',
    EDIT_UNPLANNED: 'edit-unplanned',
    UNKNOWN: 'unknown', //Could be because of error or items still loading
} as const

export type BasisroosterSaveScenario = (typeof BASISROOSTER_SAVE_SCENARIO)[keyof typeof BASISROOSTER_SAVE_SCENARIO]

/**
 * Composable for managing a multi-step wizard modal.
 *
 * Supports both declarative (via stepsConfig) and imperative (via goToStep) navigation.
 * The user can go back through the step history at any time.
 *
 * @example
 * // Declarative config (optional)
 * const wizard = useWizardModal({
 *   'step-one': { getNextStep: () => someCondition ? 'step-two' : 'step-three' },
 *   'step-two': { getNextStep: () => 'step-three' },
 *   'step-three': {},
 * })
 *
 * // Imperative navigation
 * wizard.open('step-one')
 * wizard.goToStep('step-two')
 * wizard.back()
 * wizard.close()
 *
 * // In template: use wizard.isStep('step-name') for v-if, it's reactive
 */
export function useWizardModal(stepsConfig?: WizardStepsConfig) {
    const isOpen = ref(false)
    const currentStep = ref('')
    const history = ref<string[]>([])

    const canGoBack = computed(() => history.value.length > 0)

    // Clean up state when modal is closed externally (e.g. overlay click, escape key)
    watch(isOpen, (newVal) => {
        if (!newVal) {
            currentStep.value = ''
            history.value = []
        }
    })

    /** Open the wizard at the given step, resetting history */
    function open(stepName: string) {
        currentStep.value = stepName
        history.value = []
        isOpen.value = true
    }

    /** Close the wizard and reset all state */
    function close() {
        isOpen.value = false
    }

    /** Navigate to a named step, pushing current step onto history */
    function goToStep(name: string) {
        if (currentStep.value) {
            history.value = [...history.value, currentStep.value]
        }
        currentStep.value = name
    }

    /** Navigate to the next step using the declarative getNextStep resolver for the current step */
    function next() {
        const config = stepsConfig?.[currentStep.value]
        const nextStepName = config?.getNextStep?.()
        if (nextStepName) {
            goToStep(nextStepName)
        }
    }

    /** Go back to the previous step in history */
    function back() {
        if (history.value.length > 0) {
            const newHistory = [...history.value]
            currentStep.value = newHistory.pop()!
            history.value = newHistory
        }
    }

    /** Check if the given step is currently active (reactive in templates) */
    function isStep(stepName: string): boolean {
        return currentStep.value === stepName
    }

    return {
        /** Ref controlling modal visibility. Bind to v-model on VueFinalModal via a top-level alias. */
        isOpen,
        /** The name of the currently active step */
        currentStep,
        /** Whether there is history to go back to */
        canGoBack,
        open,
        close,
        goToStep,
        next,
        back,
        isStep,
    }
}
